import { useMutation, useQuery } from '@apollo/client';
import React, { useState } from 'react';
import { Alert, Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import LoadingPage from '@website/components/utilities/LoadingPage';
import ErrorPage from '@website/components/utilities/ErrorPage';
import PriceField from '@website/components/forms/fields/PriceField';
import { SubmitButton } from '@website/components/forms/fields/SubmitButton';
import { AdminButton } from '@website/components/admin/AdminButton';
import ConfirmModal from '../../../../utilities/ConfirmModal';
import {
  DiscretionaryRefundOptionsDocument,
  DiscretionaryRefundOptionsQuery,
  AdminDiscretionaryRefundInput,
  AdminDiscretionaryRefundDocument,
  BookingSpaceInvoiceLineType,
} from '../../../../../types/graphqlOperations';

/**
 * Get the maximum refunds possible for each space
 *
 * Creates the default input values for the mutation, with the maximum amount possible for each refund.
 */
export function getSpaceMaximumRefunds(
  booking: DiscretionaryRefundOptionsQuery['booking'],
) {
  return booking.spaces.map((space) => {
    const guestAmount = space.invoice.reduce(
      (
        prev: number,
        line: DiscretionaryRefundOptionsQuery['booking']['spaces'][0]['invoice'][0],
      ): number => {
        if (line.type === BookingSpaceInvoiceLineType.TRANSACTION) {
          return prev - line.guestAmount.amountZD;
        }
        return prev;
      },
      0,
    );

    const hostAmount = space.invoice.reduce(
      (
        prev: number,
        line: DiscretionaryRefundOptionsQuery['booking']['spaces'][0]['invoice'][0],
      ): number => {
        if (line.type === BookingSpaceInvoiceLineType.TRANSACTION) {
          return prev - line.hostAmount.amountZD;
        }
        return prev;
      },
      0,
    );

    return { id: space.id, guestAmount, hostAmount };
  });
}

/**
 * Discretionary refund form
 */
export function DiscretionaryRefundForm({
  booking,
}: {
  booking: DiscretionaryRefundOptionsQuery['booking'];
}) {
  // Setup the mutation
  const [adminDiscretionaryRefund] = useMutation(
    AdminDiscretionaryRefundDocument,
  );

  const maximumValues = getSpaceMaximumRefunds(booking);

  // Setup react-hook-form
  const {
    formState: { isSubmitting, isSubmitSuccessful },
    handleSubmit,
    control,
    setError,
  } = useForm<AdminDiscretionaryRefundInput>({
    defaultValues: {
      bookingID: booking.id,
      // Default values as 0
      spaces: maximumValues.map((i) => ({
        ...i,
        guestAmount: 0,
        hostAmount: 0,
      })),
    },
  });

  // Show success if successful
  if (isSubmitSuccessful) {
    return <Alert variant="success">Success</Alert>;
  }

  const { currency } = booking.spaces[0].invoice[0].guestAmount;

  return (
    <Form
      onSubmit={handleSubmit(async (input) => {
        try {
          await adminDiscretionaryRefund({ variables: { input } });
        } catch (e) {
          setError('bookingID', {}); // Set an error so the form doesn't go to success status (doesn't matter where)
          alert(e);
        }
      })}
    >
      {maximumValues.map((space, index) => (
        <div key={space.id}>
          <h4>Space {index + 1}</h4>

          <Form.Group controlId={`spaces.${index}.guestAmount`}>
            <Form.Label>
              <strong>Guest Refund</strong>
            </Form.Label>
            <PriceField
              currency={currency}
              name={`spaces.${index}.guestAmount`}
              control={control}
            />

            <Form.Text>
              Max {space.guestAmount / 100}. This is the amount that the guest
              will be refunded, for this space.
            </Form.Text>
          </Form.Group>

          <br />

          <Form.Group controlId={`spaces.${index}.hostAmount`}>
            <Form.Label>
              <strong>Host Reclaim Amount</strong>
            </Form.Label>
            <PriceField
              currency={currency}
              name={`spaces.${index}.hostAmount`}
              control={control}
            />
            <Form.Text>
              Max {space.hostAmount / 100} (note this can't be larger than the
              guest refund). This is the amount that the host will be charged
              for (if it is set to 0, Skyhook will pay the full cost of the
              refund).
            </Form.Text>
          </Form.Group>
        </div>
      ))}

      <br />

      <SubmitButton isSubmitting={isSubmitting} />
    </Form>
  );
}

/**
 * Discretionary Refund Modal Inner
 */
export function DiscretionaryRefundModal(props: { bookingID: string }) {
  // Get the transaction details for each space
  const { data, loading, error } = useQuery(
    DiscretionaryRefundOptionsDocument,
    {
      variables: { id: props.bookingID },
      fetchPolicy: 'network-only',
    },
  );

  if (loading) return <LoadingPage />;
  if (error) return <ErrorPage error={error} />;

  return (
    <>
      <Alert variant="danger">
        <p>
          <strong>This feature is for exceptional circumstances</strong>.
        </p>
        <p>
          For example, if the guest had a bad experience on their trip, you may
          want to issue a partial refund. Alternatively, if a guest has already
          cancelled their booking and didn't get a refund, you can use this to
          retrospectively give them a refund.
        </p>
        <p>
          For each space in the booking, you can choose to refund a set amount
          to the person who booked. You can either choose to refund this
          directly from Skyhook (by setting the host reclaim amount to 0), or
          you can choose to reclaim some of this from the host.
        </p>

        <p>
          You choose how much to refund by space, as this is how it's stored in
          our systems (a booking can have several spaces, if they have booked
          for 2+ people). If you want to refund multiple spaces, we recommend
          you do this all in one go.
        </p>
      </Alert>
      <DiscretionaryRefundForm booking={data!.booking} />
    </>
  );
}

/**
 * Discretionary Refund
 *
 * Creates a button that opens a modal with the feature.
 */
export default function DiscretionaryRefund({
  bookingID,
}: {
  bookingID: string;
}) {
  const [cancelModal, showCancelModal] = useState({ visible: false });

  return (
    <>
      <AdminButton
        onClick={() => showCancelModal({ visible: true })}
        className="mb-2 me-2"
      >
        Discretionary Refund
      </AdminButton>
      <ConfirmModal
        visible={cancelModal.visible}
        setVisible={showCancelModal}
        title="Discretionary Refund (Admin)"
        body={<DiscretionaryRefundModal bookingID={bookingID} />}
        footerButtons={false}
      />
    </>
  );
}
